'use client';

import { useEffect, useMemo, useRef, useState, type ReactNode } from 'react';

import { TerminalIcon, type LucideIcon } from 'lucide-react';

import { useDebugMode } from '@/components/providers/debug-mode-provider';
import { ChevronToggle } from '@/components/ui/chevron-toggle';
import { CodeBlock } from '@/components/ui/code-block';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';
import { cn } from '@/lib/utils';
import { scrollToElementInParent } from '@/lib/utils/dom';

interface ToolStatus {
  type: 'running' | 'complete' | 'incomplete' | 'requires-action';
  reason?: string;
  error?: unknown;
}

interface ToolDebugInfoProps {
  toolName: string;
  args: unknown;
  result: unknown;
  status: ToolStatus;
  children: ReactNode;
}

const ROW_CLASS =
  'aui-tool-debug-info rounded-lg border border-dashed border-muted-foreground/30 bg-muted/30 text-left';
const HEADER_CLASS = 'flex w-full items-center gap-2 px-3 py-2 text-xs text-muted-foreground';

function DebugHeader({ icon: Icon, label, status }: DebugLabelProps) {
  return (
    <>
      <Icon className="size-3" />
      <span className="font-mono font-medium">{label}</span>
      <span
        className={cn(
          'rounded px-1.5 py-0.5 text-[10px] font-medium',
          status.type === 'complete' && 'bg-green-500/20 text-green-700 dark:text-green-400',
          status.type === 'running' && 'bg-yellow-500/20 text-yellow-700 dark:text-yellow-400',
          status.type === 'incomplete' && 'bg-red-500/20 text-red-700 dark:text-red-400',
        )}
      >
        {status.reason ?? status.type}
      </span>
    </>
  );
}

interface DebugLabelProps {
  icon: LucideIcon;
  label: string;
  status: ToolStatus;
}

/** A debug row with nothing worth expanding: header only. */
export function ToolDebugLabel(props: DebugLabelProps) {
  const { isDebugMode } = useDebugMode();
  if (!isDebugMode) return null;
  return (
    <div className="mb-3">
      <div dir="ltr" className={cn(ROW_CLASS, HEADER_CLASS)}>
        <DebugHeader {...props} />
      </div>
    </div>
  );
}

/**
 * Wraps tool UI content with an optional debug collapsible.
 * Always renders the wrapper div to keep DOM structure stable
 * across hydration — the debug collapsible only appears when
 * debug mode is active.
 */
export function ToolDebugInfo({ toolName, args, result, status, children }: ToolDebugInfoProps) {
  const { isDebugMode } = useDebugMode();
  const containerRef = useRef<HTMLDivElement>(null);
  const [isOpen, setIsOpen] = useState(false);

  const argsJson = useMemo(
    () => (isDebugMode ? JSON.stringify(args, null, 2) : ''),
    [isDebugMode, args],
  );
  const resultJson = useMemo(
    () => (isDebugMode && result !== undefined ? JSON.stringify(result, null, 2) : null),
    [isDebugMode, result],
  );

  // Scroll to element after opening (no animation, so immediate scroll works)
  useEffect(() => {
    if (!isOpen) return;
    scrollToElementInParent(containerRef.current);
  }, [isOpen]);

  return (
    <div className={cn(isDebugMode && 'mb-3 space-y-2')}>
      {isDebugMode && (
        <Collapsible
          open={isOpen}
          onOpenChange={setIsOpen}
          ref={containerRef}
          dir="ltr"
          className={ROW_CLASS}
        >
          <CollapsibleTrigger className={cn(HEADER_CLASS, 'hover:text-foreground')}>
            <DebugHeader icon={TerminalIcon} label={toolName} status={status} />
            <span className="grow" />
            <ChevronToggle isOpen={isOpen} />
          </CollapsibleTrigger>

          <CollapsibleContent className="animate-none! space-y-3 border-t border-dashed border-muted-foreground/30 p-3">
            <CodeBlock language="arguments" code={argsJson} className="text-xs" />

            {resultJson && (
              <CodeBlock language="result" code={resultJson} className="max-h-64 text-xs" />
            )}
          </CollapsibleContent>
        </Collapsible>
      )}

      {children}
    </div>
  );
}
